<template>
  <el-card
    shadow="never"
    class="monitoring-detail-panel"
  >
    <div
      slot="header"
      class="monitoring-detail-panel__header"
    >
      <span class="title">
        {{ $t('monitoringDashboard.detail.title') }}
      </span>
      <el-button
        type="text"
        icon="el-icon-close"
        @click="$emit('close')"
      >
        {{ $t('monitoringDashboard.detail.close') }}
      </el-button>
    </div>

    <!-- One chip per open drill-down -->
    <div class="monitoring-detail-tabs">
      <el-tag
        v-for="tab in tabs"
        :key="tab.key"
        :effect="tab.key === activeKey ? 'dark' : 'plain'"
        closable
        class="monitoring-detail-tab"
        @click.native="activeKey = tab.key"
        @close="closeTab(tab.key)"
      >
        {{ tab.drill.label }} ({{ tab.drill.count }})
      </el-tag>
    </div>

    <el-table
      v-if="activeTab"
      ref="detailTable"
      v-loading="activeTab.loading"
      :class="{ 'monitoring-detail-table--expandable': isAwaitingTab }"
      :data="displayRows"
      :row-key="rowKey"
      :row-class-name="rowClassName"
      :expand-row-keys="activeTab.expandedKeys"
      size="small"
      border
      :span-method="spanMethod"
      :empty-text="$t('monitoringDashboard.detail.empty')"
      @sort-change="debouncedSort"
      @expand-change="onExpandChange"
      @row-click="onRowClick"
    >
      <!-- block 4 only: the sample's analyses, inline in the expanded row -->
      <el-table-column
        v-if="isAwaitingTab"
        type="expand"
        width="36"
      >
        <template slot-scope="{ row }">
          <div
            v-loading="analysesFor(row).loading"
            class="monitoring-analyses"
          >
            <el-table
              :data="analysesFor(row).rows"
              size="mini"
              border
              :empty-text="$t('monitoringDashboard.detail.analyses.empty')"
            >
              <el-table-column
                prop="code"
                :label="$t('monitoringDashboard.detail.analyses.columns.code')"
                class-name="monitoring-analyses__code"
                label-class-name="monitoring-analyses__code"
                align="left"
                width="200"
              />
              <el-table-column
                prop="description"
                :label="
                  $t('monitoringDashboard.detail.analyses.columns.description')
                "
              />
              <el-table-column
                :label="
                  $t('monitoringDashboard.detail.analyses.columns.methodCode')
                "
                width="160"
              >
                <template slot-scope="{ row: analysis }">
                  {{ analysis.methodCode || '—' }}
                </template>
              </el-table-column>
              <el-table-column
                :label="
                  $t('monitoringDashboard.detail.analyses.columns.status')
                "
                width="150"
              >
                <template slot-scope="{ row: analysis }">
                  <span
                    class="monitoring-status-pill"
                    :class="`monitoring-status-pill--${analysisStatusDesc(
                      analysis.status
                    )}`"
                  >
                    {{ analysisStatusLabel(analysis.status) }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        v-if="isAcceptanceTab"
        width="90"
        align="center"
      >
        <template slot-scope="{ row }">
          <MonitoringSkeleton v-if="row.acc && row.acc.isSkeleton" />
          <span
            v-else-if="row.acc && row.acc.restCount"
            class="monitoring-accordion-toggle"
          >
            <i
              class="el-icon-arrow-right"
              :class="{ 'is-open': row.acc.isOpen }"
            />
            <span class="monitoring-accordion-count"
              >+{{ row.acc.restCount }}</span
            >
          </span>
        </template>
      </el-table-column>
      <!-- First column when there is no expander, so it carries the skeleton. -->
      <el-table-column
        prop="orderId"
        :label="$t('monitoringDashboard.detail.columns.orderId')"
      >
        <template slot-scope="{ row }">
          <MonitoringSkeleton v-if="row.acc && row.acc.isSkeleton" />
          <span v-else>{{ row.orderId }}</span>
        </template>
      </el-table-column>
      <el-table-column
        prop="groupingId"
        :label="$t('monitoringDashboard.detail.columns.groupingId')"
      />
      <el-table-column
        prop="labSampleCode"
        :label="$t('monitoringDashboard.detail.columns.labSampleCode')"
      />
      <el-table-column :label="$t('monitoringDashboard.detail.columns.status')">
        <template slot-scope="{ row }">
          <span
            class="monitoring-status-pill"
            :class="`monitoring-status-pill--${statusDesc(row.status)}`"
          >
            {{ statusLabel(row.status) }}
          </span>
        </template>
      </el-table-column>
      <!-- acceptance (block 1) only -->
      <el-table-column
        v-if="isAcceptanceTab"
        prop="customerId"
        :label="$t('monitoringDashboard.detail.columns.customerId')"
      />
      <el-table-column
        v-if="isAcceptanceTab"
        prop="customerName"
        :label="$t('monitoringDashboard.detail.columns.customerName')"
      >
        <template slot-scope="{ row }">
          {{ row.customerName || '—' }}
        </template>
      </el-table-column>
      <!-- blocks 1–3 only -->
      <el-table-column
        v-if="!isAwaitingTab"
        :label="$t('monitoringDashboard.detail.columns.location')"
      >
        <template slot-scope="{ row }">
          {{ (row.location && row.location.name) || row.locationName || '—' }}
        </template>
      </el-table-column>
      <el-table-column
        v-if="!isAwaitingTab"
        prop="laboratory"
        :label="$t('monitoringDashboard.detail.columns.laboratory')"
      />
      <el-table-column
        v-if="!isAwaitingTab"
        :label="dateColumnLabel"
        :prop="dateField"
        :sortable="isAcceptanceTab ? false : 'custom'"
      >
        <template slot-scope="{ row }">
          {{ formatDate(row[dateField]) }}
        </template>
      </el-table-column>

      <!-- block 4 (awaiting processing) only -->
      <el-table-column
        v-if="isAwaitingTab"
        prop="customerId"
        :label="$t('monitoringDashboard.detail.columns.customer')"
      />
      <el-table-column
        v-if="isAwaitingTab"
        prop="productName"
        :label="$t('monitoringDashboard.detail.columns.productName')"
      />
      <el-table-column
        v-if="isAwaitingTab"
        prop="customerProductName"
        :label="$t('monitoringDashboard.detail.columns.customerProductName')"
      >
        <template slot-scope="{ row }">
          {{ row.customerProductName || '—' }}
        </template>
      </el-table-column>
      <el-table-column
        v-if="isAwaitingTab"
        prop="orderSentAt"
        :label="$t('monitoringDashboard.detail.columns.acceptanceDate')"
        sortable="custom"
      >
        <template slot-scope="{ row }">
          {{ formatDate(row.orderSentAt) }}
        </template>
      </el-table-column>
      <el-table-column
        v-if="isAwaitingTab"
        prop="acceptedAt"
        :label="$t('monitoringDashboard.detail.columns.acceptedAt')"
        sortable="custom"
      >
        <template slot-scope="{ row }">
          {{ formatDate(row.acceptedAt) }}
        </template>
      </el-table-column>
      <el-table-column
        v-if="isAwaitingTab"
        prop="expectedDeliveryDate"
        :label="$t('monitoringDashboard.detail.columns.deliveryDate')"
        sortable="custom"
      >
        <template slot-scope="{ row }">
          {{ formatDate(row.expectedDeliveryDate) }}
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('monitoringDashboard.detail.columns.daysWaiting')"
        width="110"
        align="center"
      >
        <template slot-scope="{ row }">
          <span
            v-if="calcDaysWaiting(row) !== null"
            :class="daysWaitingClass(calcDaysWaiting(row))"
          >
            {{ calcDaysWaiting(row) }}
          </span>
          <span v-else>—</span>
        </template>
      </el-table-column>

      <!-- shared navigate button -->
      <el-table-column
        width="60"
        align="center"
      >
        <template slot-scope="{ row }">
          <el-button
            type="text"
            icon="el-icon-arrow-right"
            @click.stop="$emit('open-sample', row, activeTab.drill)"
          />
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="activeTab"
      class="monitoring-detail-pagination"
      layout="prev, pager, next"
      :total="activeTab.total"
      :page-size="limit"
      :current-page="activeTab.page"
      @current-change="onPageChange"
    />
  </el-card>
</template>

<script>
  import axios from 'axios';
  import debounce from 'lodash.debounce';
  import { samplesApi, monitoringApi } from '@/api';
  import {
    LAB_SAMPLE_STATUSES_DESC,
    SAMPLE_ANALYSIS_STATUSES_DESC,
  } from '@/config/constants';
  import {
    MONITORING_BLOCKS,
    DELIVERY_DATE_FIELD,
    ACCEPTANCE_DATE_FIELD,
    EXPORT_BLOCK_PARAM,
    ACCEPTANCE_BUCKET_PARAM,
    buildDetailWhere,
  } from '@/config/monitoringDashboard.config';
  import MonitoringSkeleton from './MonitoringSkeleton.vue';

  const PAGE_SIZE = 20;
  const SKELETON_ROWS = 5;
  const SKELETON_COLSPAN = 99;

  /** Shown by a row whose analyses have not been requested yet. */
  const NO_ANALYSES = { loading: false, rows: [] };

  function parseDateOnly(dateVal) {
    const d = new Date(dateVal);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function skeletonRows(scope, count, extra = {}) {
    return Array.from({ length: count }, (unused, index) => ({
      acc: { ...extra, isSkeleton: true, key: `${scope}-${index}` },
    }));
  }

  function tabKey(drill) {
    return [
      drill.blockKey,
      drill.statusKey,
      drill.bucketKey,
      drill.laboratory,
      drill.familyCode,
    ].join('|');
  }

  export default {
    name: 'MonitoringDetailPanel',
    components: { MonitoringSkeleton },
    props: {
      // Canonical filters: { locationIds: number[], laboratories: string[] }
      filters: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        tabs: [],
        activeKey: null,
        limit: PAGE_SIZE,
        debouncedSort: null,
        openOrderId: null,
      };
    },
    created() {
      this.debouncedSort = debounce(this.onSortChange, 300);
    },
    watch: {
      activeKey() {
        this.openOrderId = null;
      },
    },
    computed: {
      activeTab() {
        return this.tabs.find((t) => t.key === this.activeKey) || null;
      },
      // el-table has no nested rows, so the open order's samples are flattened
      // into the row list right after their header.
      displayRows() {
        if (!this.activeTab) return [];
        if (this.activeTab.loading) return skeletonRows('page', SKELETON_ROWS);
        if (!this.isAcceptanceTab) return this.activeTab.rows;
        return this.activeTab.rows.flatMap((header) => this.groupRows(header));
      },
      isAcceptanceTab() {
        return (
          !!this.activeTab &&
          this.activeTab.drill.blockKey === MONITORING_BLOCKS.acceptance
        );
      },
      isAwaitingTab() {
        return (
          !!this.activeTab &&
          this.activeTab.drill.blockKey === MONITORING_BLOCKS.awaitingProcessing
        );
      },
      dateField() {
        return this.isAcceptanceTab
          ? ACCEPTANCE_DATE_FIELD
          : DELIVERY_DATE_FIELD;
      },
      dateColumnLabel() {
        return this.isAcceptanceTab
          ? this.$t('monitoringDashboard.detail.columns.acceptanceDate')
          : this.$t('monitoringDashboard.detail.columns.deliveryDate');
      },
    },
    methods: {
      // --- public API (called by the orchestrator via $refs) ----------------
      openDrill(drill) {
        const key = tabKey(drill);
        const existing = this.tabs.find((t) => t.key === key);
        if (existing) {
          // Refresh count/label in case the same cell was re-clicked after a reload.
          existing.drill = drill;
          this.activeKey = key;
          this.loadTab(existing);
          return;
        }
        const tab = {
          key,
          drill,
          rows: [],
          total: 0,
          loading: false,
          page: 1,
          order: null,
          abortController: null,
          // labSampleId → { loading, rows, abortController }, filled on expand
          analyses: {},
          expandedKeys: [],
          samplesByOrder: {},
          samplesLoading: {},
        };
        this.tabs.push(tab);
        this.activeKey = key;
        this.loadTab(tab);
      },
      refreshOpenTabs() {
        this.tabs.forEach((tab) => {
          // eslint-disable-next-line no-param-reassign
          tab.page = 1;
          this.loadTab(tab);
        });
      },
      buildExportParams() {
        const tab = this.activeTab;
        if (!tab) return null;

        const params = { block: EXPORT_BLOCK_PARAM[tab.drill.blockKey] };
        if (this.filters.locationIds && this.filters.locationIds.length) {
          params.locationIds = this.filters.locationIds;
        }

        if (tab.drill.blockKey === MONITORING_BLOCKS.awaitingProcessing) {
          params.laboratory = tab.drill.laboratory;
          params.familyCode = tab.drill.familyCode;
          params.bucket = tab.drill.bucketKey;
        } else {
          params.where = buildDetailWhere(tab.drill, this.filters);
        }

        return params;
      },
      // ----------------------------------------------------------------------
      closeTab(key) {
        const idx = this.tabs.findIndex((t) => t.key === key);
        if (idx === -1) return;
        if (this.tabs[idx].abortController)
          this.tabs[idx].abortController.abort();
        this.resetAnalyses(this.tabs[idx]);
        this.tabs.splice(idx, 1);
        if (this.activeKey === key) {
          this.activeKey = this.tabs.length ? this.tabs[0].key : null;
        }
        if (!this.tabs.length) this.$emit('close');
      },
      tableFilters(tab) {
        const filter = {
          skip: (tab.page - 1) * this.limit,
          limit: this.limit,
        };
        if (tab.order) filter.order = tab.order;
        return filter;
      },
      acceptanceParams(tab) {
        const params = { bucket: ACCEPTANCE_BUCKET_PARAM[tab.drill.bucketKey] };
        if (this.filters.locationIds && this.filters.locationIds.length) {
          params.locationIds = this.filters.locationIds;
        }
        return params;
      },
      withStatus(data) {
        const raw = Array.isArray(data) ? data : data.rows || [];
        return raw.map((r) => ({ ...r, status: r.labSampleStatus }));
      },
      async loadOrderSamples(tab, header) {
        const { orderId } = header;
        if (tab.samplesByOrder[orderId] || tab.samplesLoading[orderId]) return;

        this.$set(tab.samplesLoading, orderId, true);
        try {
          const response = await monitoringApi.pendingAcceptanceOrderSamples(
            orderId,
            {
              ...this.acceptanceParams(tab),
              filter: { order: ['labSampleCode ASC'] },
            },
            { signal: tab.abortController.signal }
          );
          // The response includes the sample shown as the group row.
          this.$set(
            tab.samplesByOrder,
            orderId,
            this.withStatus(response.data).filter((s) => s.id !== header.id)
          );
        } catch (error) {
          if (!axios.isCancel(error)) {
            this.$notify({
              showClose: true,
              type: 'error',
              message:
                (error.response &&
                  error.response.data &&
                  error.response.data.error &&
                  error.response.data.error.message) ||
                error.message,
            });
          }
        } finally {
          this.$set(tab.samplesLoading, orderId, false);
        }
      },
      async loadTab(tab) {
        // eslint-disable-next-line no-param-reassign
        tab.loading = true;
        // Any reload replaces the rows, so drop the analyses cached against the
        // previous ones rather than risk showing them under a refreshed sample.
        this.resetAnalyses(tab);
        // Refetching replaces the rows, so any open group must collapse with them.
        this.openOrderId = null;
        // eslint-disable-next-line no-param-reassign
        tab.samplesByOrder = {};
        // eslint-disable-next-line no-param-reassign
        tab.samplesLoading = {};

        // Per-tab controller, not abortControllerMixin: refreshOpenTabs() fires
        // loadTab() for every open tab without awaiting each one, so their
        // requests end up in flight at the same time, a single shared
        // controller would abort one tab's request when another tab's starts.

        if (tab.abortController) tab.abortController.abort();
        // eslint-disable-next-line no-param-reassign
        tab.abortController = new AbortController();
        const { signal } = tab.abortController;

        let aborted = false;

        try {
          let rows = [];
          if (tab.drill.blockKey === MONITORING_BLOCKS.awaitingProcessing) {
            const filter = this.tableFilters(tab);
            if (filter.order) filter.order = [filter.order];
            const baseParams = {
              laboratory: tab.drill.laboratory,
              familyCode: tab.drill.familyCode,
              bucket: tab.drill.bucketKey,
            };
            if (this.filters.locationIds && this.filters.locationIds.length) {
              baseParams.locationIds = this.filters.locationIds;
            }
            const [response, countResponse] = await Promise.all([
              monitoringApi.awaitingProcessingDetail(
                { ...baseParams, filter },
                { signal }
              ),
              monitoringApi.awaitingProcessingDetailCount(baseParams, {
                signal,
              }),
            ]);
            rows = this.withStatus(response.data);
            // eslint-disable-next-line no-param-reassign
            tab.total = countResponse.data.count;
            // eslint-disable-next-line no-param-reassign
            tab.drill = { ...tab.drill, count: countResponse.data.count };
          } else if (tab.drill.blockKey === MONITORING_BLOCKS.acceptance) {
            // skip/limit page ORDERS, so the count is the number of orders and
            // the card's own sample count stays on the tab chip.
            const baseParams = this.acceptanceParams(tab);
            const [response, countResponse] = await Promise.all([
              monitoringApi.pendingAcceptanceDetail(
                {
                  ...baseParams,
                  skip: (tab.page - 1) * this.limit,
                  limit: this.limit,
                },
                { signal }
              ),
              monitoringApi.pendingAcceptanceDetailCount(baseParams, {
                signal,
              }),
            ]);
            rows = this.withStatus(response.data);
            // eslint-disable-next-line no-param-reassign
            tab.total = countResponse.data.count;
          } else {
            const where = buildDetailWhere(tab.drill, this.filters);
            const [response, countResponse] = await Promise.all([
              samplesApi.indexWhere(this.tableFilters(tab), where, { signal }),
              samplesApi.countWhere(where, { signal }),
            ]);
            rows = response.data;
            // eslint-disable-next-line no-param-reassign
            tab.total = countResponse.data.count;
            // eslint-disable-next-line no-param-reassign
            tab.drill = { ...tab.drill, count: countResponse.data.count };
          }
          // eslint-disable-next-line no-param-reassign
          tab.rows = rows;
        } catch (error) {
          if (axios.isCancel(error)) {
            aborted = true;
            return;
          }
          this.notifyError(error);
        } finally {
          if (!aborted) {
            // eslint-disable-next-line no-param-reassign
            tab.loading = false;
          }
        }
      },
      notifyError(error) {
        this.$notify({
          showClose: true,
          type: 'error',
          message:
            (error.response &&
              error.response.data &&
              error.response.data.error &&
              error.response.data.error.message) ||
            error.message,
        });
      },
      // --- inline analyses of an expanded sample (block 4 only) --------------
      analysesFor(row) {
        return (
          (this.activeTab && this.activeTab.analyses[row.id]) || NO_ANALYSES
        );
      },
      /**
       * Clicking anywhere on the row toggles the expansion, so the whole row is
       * the affordance and not just the arrow. The expand cell already toggles
       * itself, and the navigate button stops its own click.
       */
      /**
       * Dispatches per tab type: block 4 toggles the inline analyses expander,
       * the acceptance accordion toggles its order group.
       */
      onRowClick(row, column) {
        if (this.isAwaitingTab) {
          if (column && column.type === 'expand') return;
          this.$refs.detailTable.toggleRowExpansion(row);
          return;
        }
        // Only multi-sample group headers toggle; single-sample rows and
        // children are inert. Opening one closes the previously open group.
        const { acc } = row;
        if (!acc || !acc.isHeader || !acc.restCount) return;
        if (this.openOrderId === acc.orderId) {
          this.openOrderId = null;
          return;
        }
        this.openOrderId = acc.orderId;
        this.loadOrderSamples(this.activeTab, row);
      },
      // Plain (non-tree) el-table hands us the full list of expanded rows here.
      onExpandChange(row, expandedRows) {
        const tab = this.activeTab;
        if (!tab) return;
        tab.expandedKeys = expandedRows.map((r) => r.id);

        if (!tab.expandedKeys.includes(row.id)) {
          this.abortAnalyses(tab.analyses[row.id]);
          this.$delete(tab.analyses, row.id);
          return;
        }
        if (!tab.analyses[row.id]) this.loadAnalyses(tab, row);
      },
      async loadAnalyses(tab, row) {
        const abortController = new AbortController();
        this.$set(tab.analyses, row.id, {
          loading: true,
          rows: [],
          abortController,
        });

        const params = {
          laboratory: tab.drill.laboratory,
          familyCode: tab.drill.familyCode,
          bucket: tab.drill.bucketKey,
        };
        if (this.filters.locationIds && this.filters.locationIds.length) {
          params.locationIds = this.filters.locationIds;
        }

        try {
          const { data } = await monitoringApi.awaitingProcessingSampleAnalyses(
            row.id,
            params,
            { signal: abortController.signal }
          );
          this.$set(tab.analyses, row.id, { loading: false, rows: data || [] });
        } catch (error) {
          if (axios.isCancel(error)) return;
          this.$set(tab.analyses, row.id, { loading: false, rows: [] });
          this.notifyError(error);
        }
      },
      abortAnalyses(state) {
        if (state && state.abortController) state.abortController.abort();
      },
      resetAnalyses(tab) {
        Object.keys(tab.analyses).forEach((id) => {
          this.abortAnalyses(tab.analyses[id]);
          this.$delete(tab.analyses, id);
        });
        // eslint-disable-next-line no-param-reassign
        tab.expandedKeys = [];
      },
      analysisStatusDesc(status) {
        return SAMPLE_ANALYSIS_STATUSES_DESC[status] || '';
      },
      analysisStatusLabel(status) {
        const desc = this.analysisStatusDesc(status);
        return desc ? this.$t(`enums.labSampleAnalysisStatuses.${desc}`) : '';
      },
      // ----------------------------------------------------------------------
      onSortChange({ prop, order }) {
        if (!this.activeTab) return;
        this.activeTab.order =
          order && prop
            ? `${prop} ${order === 'ascending' ? 'ASC' : 'DESC'}`
            : null;
        this.activeTab.page = 1;
        this.loadTab(this.activeTab);
      },
      onPageChange(page) {
        if (!this.activeTab) return;
        this.activeTab.page = page;
        this.loadTab(this.activeTab);
      },
      rowClassName({ row }) {
        const { acc } = row;
        if (!acc) return '';
        if (acc.isHeader && acc.restCount) return 'is-accordion-header';
        if (acc.isChild) return 'is-accordion-child';
        return '';
      },
      groupRows(header) {
        const { samplesByOrder, samplesLoading } = this.activeTab;
        const { orderId } = header;
        const restCount = Math.max((header.sampleCount || 1) - 1, 0);
        const isOpen = this.openOrderId === orderId;
        const group = [
          { ...header, acc: { orderId, isHeader: true, restCount, isOpen } },
        ];
        if (!isOpen) return group;
        if (samplesLoading[orderId]) {
          return group.concat(
            skeletonRows(
              `order-${orderId}`,
              Math.min(restCount, SKELETON_ROWS),
              { orderId, isChild: true }
            )
          );
        }
        return group.concat(
          (samplesByOrder[orderId] || []).map((row) => ({
            ...row,
            acc: { orderId, isChild: true },
          }))
        );
      },
      rowKey(row) {
        const { acc } = row;
        if (acc && acc.isSkeleton) return acc.key;
        return row.id || row.labSampleId || row.labSampleCode;
      },
      spanMethod({ row, columnIndex }) {
        if (!row.acc || !row.acc.isSkeleton) return [1, 1];
        return columnIndex === 0 ? [1, SKELETON_COLSPAN] : [0, 0];
      },
      statusDesc(status) {
        return LAB_SAMPLE_STATUSES_DESC[status] || '';
      },
      statusLabel(status) {
        const desc = this.statusDesc(status);
        return desc ? this.$t(`enums.sampleStatuses.${desc}`) : '';
      },
      calcDaysWaiting(row) {
        const blockKey = this.activeTab && this.activeTab.drill.blockKey;
        let dateVal;
        let sign = 1;

        if (blockKey === MONITORING_BLOCKS.awaitingProcessing) {
          dateVal = row.acceptedAt;
        } else if (blockKey === MONITORING_BLOCKS.acceptance) {
          dateVal = row[ACCEPTANCE_DATE_FIELD];
        } else if (blockKey === MONITORING_BLOCKS.closing) {
          dateVal = row[DELIVERY_DATE_FIELD];
          sign = -1; // days remaining: positive = still ahead
        } else {
          dateVal = row[DELIVERY_DATE_FIELD];
        }

        if (!dateVal) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return sign * Math.round((today - parseDateOnly(dateVal)) / 86400000);
      },
      daysWaitingClass(days) {
        const blockKey = this.activeTab && this.activeTab.drill.blockKey;
        if (blockKey === MONITORING_BLOCKS.closing) {
          if (days >= 2) return 'cell-current';
          if (days === 1) return 'cell-imminent';
          return 'cell-overdue';
        }
        if (blockKey === MONITORING_BLOCKS.acceptance) {
          if (days <= 2) return 'cell-current';
          if (days <= 5) return 'cell-imminent';
          return 'cell-overdue';
        }
        if (blockKey === MONITORING_BLOCKS.awaitingProcessing) {
          if (days <= 3) return 'cell-current';
          if (days <= 10) return 'cell-imminent';
          return 'cell-overdue';
        }
        // expired: recently past = amber, significantly past = red
        if (days <= 5) return 'cell-imminent';
        return 'cell-overdue';
      },
      formatDate(value) {
        if (!value) return '—';
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('it-IT');
      },
    },
    beforeDestroy() {
      this.debouncedSort.cancel();
      this.tabs.forEach((tab) => {
        if (tab.abortController) tab.abortController.abort();
        Object.keys(tab.analyses).forEach((id) =>
          this.abortAnalyses(tab.analyses[id])
        );
      });
    },
  };
</script>

<style lang="scss" scoped>
  .monitoring-detail-panel {
    // el-card is `overflow: hidden`, which makes it the scrollport the table
    // header would stick to; `clip` clips the same but scrolls nothing.
    overflow: clip;
    margin-top: 16px;
    background: #2e3454;
    border: 1px solid rgba(#fff, 0.06);
    border-radius: 4px 12px 12px 4px;
    box-shadow: 0 10px 25px -5px rgba(#000, 0.3),
      0 4px 12px -2px rgba(#000, 0.2);

    ::v-deep .el-card__body {
      padding: 20px 24px;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  .title {
    color: #ffffff;
    font-weight: 600;
  }

  .monitoring-detail-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }

  .monitoring-detail-tab {
    cursor: pointer;
  }

  .monitoring-detail-pagination {
    margin-top: 12px;
    text-align: right;
  }

  // Status pill — mirrors the "Scaduti" stat-card palette (translucent fill +
  // matching border) so the detail status carries the same colour identity as
  // the block cards. Keys are the LAB_SAMPLE_STATUSES_DESC values.
  $monitoring-pill-colors: (
    'accepted': #10b981,
    'inprogress': #f59e0b,
    'toBeVerified': #6366f1,
    'toBeApproved': #64748b,
    'completed': #ef4444,
    'pending': #38bdf8,
    'suspended': #38bdf8,
    'canceled': #ef4444,
    'rejected': #ef4444,
  );

  .monitoring-status-pill {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid transparent;
    color: #f8fafc;

    @each $name, $c in $monitoring-pill-colors {
      &--#{$name} {
        background: rgba($c, 0.15);
        border-color: rgba($c, 0.4);
      }
    }
  }

  ::v-deep .el-table {
    background-color: #1e2235;
    border: 1px solid #323854;
    border-collapse: collapse;
    color: #cbd5e1;

    &::before,
    &::after {
      background-color: #323854;
    }

    thead tr th {
      background-color: #2a2f4a;
      color: #ffffff;
      font-weight: 600;
      padding: 8px 0;
      border-bottom: 1px solid #323854;
      text-align: center;
    }

    tbody tr td {
      background-color: #1e2235;
      padding: 4px 0;
      border-bottom: none;
      text-align: center;
    }

    .cell {
      text-align: center;
    }

    tbody tr:hover td {
      background-color: #2e3557;
      color: #ffffff;
    }

    &.el-table--border th,
    &.el-table--border td {
      border-right: 1px solid #323854;
    }

    .el-table__empty-block {
      background-color: #1e2235;
    }

    .el-table__empty-text {
      color: #64748b;
    }
  }

  // Expanded analyses row: the whole sample row is clickable, and the nested
  // table sits on a slightly darker surface so it reads as a child of the row
  // rather than as more sample rows. Only block 4 expands, so the clickable
  // affordance is scoped to that tab — the other blocks' rows are inert.
  .monitoring-detail-table--expandable {
    ::v-deep .el-table__body tr.el-table__row {
      cursor: pointer;
    }
  }

  ::v-deep .el-table__expanded-cell {
    background-color: #191c2b;
    padding: 12px 16px;

    &:hover {
      background-color: #191c2b !important;
    }
  }

  .monitoring-analyses {
    ::v-deep .el-table {
      background-color: #232840;

      thead tr th {
        background-color: #2f3552;
      }

      tbody tr td {
        background-color: #232840;
      }

      tbody tr:hover td {
        background-color: #343b5e;
      }

      .el-table__empty-block {
        background-color: #232840;
      }
    }

    // Analysis rows are read-only: undo the clickable cursor inherited from
    // the sample table above. Specificity must match the --expandable rule,
    // which also reaches these rows through the expanded cell.
    ::v-deep .el-table__body tr.el-table__row {
      cursor: default;
    }

    // Analysis codes are dotted identifiers of fixed width: keep them on one
    // line (el-table's default `word-break: break-all` splits them mid-code)
    // and left-aligned — the panel centres every `.cell`, which beats the
    // column's own `align="left"`.
    ::v-deep .monitoring-analyses__code .cell {
      white-space: nowrap;
      word-break: keep-all;
      text-align: left;
    }
  }

  .monitoring-accordion-toggle {
    display: inline-flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 6px;
    white-space: nowrap;

    .el-icon-arrow-right {
      transition: transform 0.2s ease;

      &.is-open {
        transform: rotate(90deg);
      }
    }
  }

  .monitoring-accordion-count {
    display: inline-block;
    white-space: nowrap;
    word-break: keep-all;
    overflow-wrap: normal;
    padding: 0 6px;
    border-radius: 8px;
    font-size: 11px;
    font-weight: 600;
    color: #e0f2fe;
    background: rgba(#38bdf8, 0.18);
    border: 1px solid rgba(#38bdf8, 0.4);
  }

  ::v-deep .el-table {
    .is-accordion-header {
      cursor: pointer;
    }

    .is-accordion-child td {
      background-color: #191d2e;

      &:first-child {
        box-shadow: inset 3px 0 0 #38bdf8;
      }
    }
  }

  .cell-overdue {
    color: #ef4444 !important;
    font-weight: 600;
  }

  .cell-imminent {
    color: #f59e0b !important;
    font-weight: 600;
  }

  .cell-current {
    color: #10b981 !important;
  }
</style>
